import React from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';

// Wyspur brand colors
const wyspurColors = {
  blue: '#0f71fb',
  orange: '#ffaa01',
  pink: '#df3e99',
  lightBlue: '#4f91ff',
  lightOrange: '#ffbb33',
  lightPink: '#e55bb3'
};

const FEATURE_COLORS = [
  wyspurColors.blue,
  wyspurColors.orange,
  wyspurColors.pink,
  wyspurColors.lightBlue,
  wyspurColors.lightOrange,
  wyspurColors.lightPink
];

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const formatNumber = (num) => {
  if (!num) return '0';
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toString();
};

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <Box
        sx={{
          backgroundColor: 'rgba(15, 113, 251, 0.1)',
          border: '1px solid rgba(15, 113, 251, 0.2)',
          borderRadius: 2,
          color: 'white',
          p: 2,
          boxShadow: 3
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
          {label}
        </Typography>
        {payload.map((entry, index) => (
          <Typography
            key={index}
            variant="body2"
            sx={{ color: entry.color, display: 'flex', alignItems: 'center', mb: 0.5 }}
          >
            <Box
              sx={{
                width: 12,
                height: 12,
                bgcolor: entry.color,
                borderRadius: '50%',
                mr: 1
              }}
            />
            {`${entry.name}: ${formatNumber(entry.value)}`}
          </Typography>
        ))}
      </Box>
    );
  }
  return null;
};

const UsageAnalytics = ({ usageTrends, featureUsage, timeframe, jobsByType }) => {
  const trendsLoading = !usageTrends || !usageTrends.success;
  const featuresLoading = !featureUsage || !featureUsage.success;
  const jobsLoading = !jobsByType || !jobsByType.success;

  // Prepare usage trends data
  const trendsData = usageTrends?.data?.map(item => ({
    date: formatDate(item.date),
    fullDate: item.date,
    'Total Actions': item.totalActions || 0,
    'Credits Used': item.creditsUsed || 0
  })) || []; // Show all available data

  // Prepare feature usage data - showing credits used instead of action counts
  const featuresData = featureUsage?.data?.map(item => {
    let displayName = item.feature?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Unknown';
    // Rename "Research Agent" to "Website Analyzer"
    if (displayName === 'Research Agent') {
      displayName = 'Website Analyzer';
    }
    return {
      name: displayName,
      value: item.creditsUsed || 0, // Changed from count to creditsUsed
      actionCount: item.count || 0, // Keep action count for reference
      credits: item.creditsUsed || 0,
      percentage: item.percentage || 0
    };
  }) || [];

  // Prepare jobs by type data
  const jobsData = jobsByType?.data?.map(item => {
    let displayName = item.type || 'Unknown';
    // Rename "Research Agent" to "Website Analyzer"
    if (displayName === 'research_agent' || displayName === 'Research Agent') {
      displayName = 'Website Analyzer';
    }
    return {
      name: displayName,
      value: item.count || 0,
      percentage: item.percentage || 0
    };
  }) || [];

  return (
    <Box>
      {/* Usage Trends - Separate from Grid */}
      <Box sx={{ mb: 3 }}>
        <Card
          elevation={3}
          sx={{
            backgroundColor: 'rgba(15, 113, 251, 0.05)',
            border: '1px solid rgba(15, 113, 251, 0.15)',
            borderRadius: 2,
            color: 'white'
          }}
        >
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2, color: wyspurColors.blue, fontWeight: 600 }}>
              Usage Trends
            </Typography>

            {trendsLoading ? (
              <Box display="flex" justifyContent="center" alignItems="center" height="500px">
                <CircularProgress sx={{ color: wyspurColors.blue }} />
              </Box>
            ) : trendsData.length === 0 ? (
              <Alert
                severity="info"
                sx={{
                  backgroundColor: `${wyspurColors.blue}15`,
                  border: `1px solid ${wyspurColors.blue}40`,
                  color: wyspurColors.blue,
                  '& .MuiAlert-icon': { color: wyspurColors.blue }
                }}
              >
                No usage data available for the selected period.
              </Alert>
            ) : (
              <ResponsiveContainer width="100%" height={500}>
                <AreaChart data={trendsData} margin={{ top: 10, right: 10, left: 10, bottom: 30 }}>
                  <defs>
                    <linearGradient id="creditsUsed" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={wyspurColors.orange} stopOpacity={0.3}/>
                      <stop offset="95%" stopColor={wyspurColors.orange} stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis
                    dataKey="date"
                    fontSize={12}
                    tick={{ fill: '#666' }}
                    angle={-45}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis
                    fontSize={12}
                    tick={{ fill: '#666' }}
                    tickFormatter={formatNumber}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Area
                    type="monotone"
                    dataKey="Credits Used"
                    stroke={wyspurColors.orange}
                    fillOpacity={1}
                    fill="url(#creditsUsed)"
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </Box>

      {/* Pie Charts Grid */}
      <Grid container spacing={3}>
        {/* Feature Usage Distribution */}
        <Grid item xs={12} lg={6}>
        <Card 
          elevation={3}
          sx={{
            backgroundColor: 'rgba(15, 113, 251, 0.05)',
            border: '1px solid rgba(15, 113, 251, 0.15)',
            borderRadius: 2,
            color: 'white'
          }}
        >
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2, color: wyspurColors.blue, fontWeight: 600 }}>
              Feature Usage Distribution
            </Typography>
            
            {featuresLoading ? (
              <Box display="flex" justifyContent="center" alignItems="center" height="300px">
                <CircularProgress sx={{ color: wyspurColors.blue }} />
              </Box>
            ) : featuresData.length === 0 ? (
              <Alert 
                severity="info"
                sx={{
                  backgroundColor: `${wyspurColors.blue}15`,
                  border: `1px solid ${wyspurColors.blue}40`,
                  color: wyspurColors.blue,
                  '& .MuiAlert-icon': { color: wyspurColors.blue }
                }}
              >
                No feature usage data available.
              </Alert>
            ) : (
              <>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={featuresData}
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {featuresData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={FEATURE_COLORS[index % FEATURE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value) => [formatNumber(value), 'Credits']}
                    />
                  </PieChart>
                </ResponsiveContainer>
                
                <Box sx={{ mt: 2 }}>
                  {featuresData.map((feature, index) => (
                    <Box key={feature.name} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          bgcolor: FEATURE_COLORS[index % FEATURE_COLORS.length],
                          borderRadius: '50%',
                          mr: 1
                        }}
                      />
                      <Typography variant="body2" sx={{ flex: 1, fontSize: '0.85rem', color: 'white' }}>
                        {feature.name}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'rgba(255, 255, 255, 0.8)' }}>
                        {formatNumber(feature.value)} credits
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </>
            )}
          </CardContent>
        </Card>
      </Grid>

      {/* Jobs by Type */}
      <Grid item xs={12} lg={6}>
        <Card 
          elevation={3}
          sx={{
            backgroundColor: 'rgba(15, 113, 251, 0.05)',
            border: '1px solid rgba(15, 113, 251, 0.15)',
            borderRadius: 2,
            color: 'white'
          }}
        >
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2, color: wyspurColors.blue, fontWeight: 600 }}>
              Jobs by Type ({timeframe === 'today' ? 'Today' : timeframe === '7days' ? 'Last 7 Days' : 'Last 30 Days'})
            </Typography>
            
            {jobsLoading ? (
              <Box display="flex" justifyContent="center" alignItems="center" height="300px">
                <CircularProgress sx={{ color: wyspurColors.blue }} />
              </Box>
            ) : jobsData.length === 0 ? (
              <Alert 
                severity="info"
                sx={{
                  backgroundColor: `${wyspurColors.blue}15`,
                  border: `1px solid ${wyspurColors.blue}40`,
                  color: wyspurColors.blue,
                  '& .MuiAlert-icon': { color: wyspurColors.blue }
                }}
              >
                No jobs data available for the selected period.
              </Alert>
            ) : (
              <>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={jobsData}
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {jobsData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={FEATURE_COLORS[index % FEATURE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value) => [formatNumber(value), 'Jobs']}
                    />
                  </PieChart>
                </ResponsiveContainer>
                
                <Box sx={{ mt: 2 }}>
                  {jobsData.map((job, index) => (
                    <Box key={job.name} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          bgcolor: FEATURE_COLORS[index % FEATURE_COLORS.length],
                          borderRadius: '50%',
                          mr: 1
                        }}
                      />
                      <Typography variant="body2" sx={{ flex: 1, fontSize: '0.85rem', color: 'white' }}>
                        {job.name}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'rgba(255, 255, 255, 0.8)' }}>
                        {formatNumber(job.value)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </>
            )}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
    </Box>
  );
};

export default UsageAnalytics;